import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronRight, ChevronLeft, Check, Shield } from 'lucide-react';
import { toast } from 'sonner';
import healthApi from '@/api/healthApi';
import { useLanguage } from '@/contexts/LanguageContext';

const AGE_RANGES = [
  { value: '18-24', label: '18-24' },
  { value: '25-34', label: '25-34' },
  { value: '35-44', label: '35-44' },
  { value: '45-54', label: '45-54' },
  { value: '55-64', label: '55-64' },
  { value: '65+', label: '65+' }
];

const GENDERS = [
  { value: 'male', label: 'Male', emoji: '👨' },
  { value: 'female', label: 'Female', emoji: '👩' },
  { value: 'non-binary', label: 'Non-binary', emoji: '🧑' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say', emoji: '🤐' }
];

const COUNTRIES = [
  { value: 'uk', label: 'United Kingdom', region: 'european' },
  { value: 'nigeria', label: 'Nigeria', region: 'west_african' },
  { value: 'ghana', label: 'Ghana', region: 'west_african' },
  { value: 'kenya', label: 'Kenya', region: 'east_african' },
  { value: 'south_africa', label: 'South Africa', region: 'southern_african' },
  { value: 'usa', label: 'United States', region: 'western' },
  { value: 'canada', label: 'Canada', region: 'western' },
  { value: 'india', label: 'India', region: 'south_asian' },
  { value: 'jamaica', label: 'Jamaica', region: 'caribbean' },
  { value: 'other', label: 'Other', region: 'general' }
];

const HEALTH_GOALS = [
  { value: 'weight_loss', label: 'Weight Loss', emoji: '⚖️', description: 'Lose weight healthily' },
  { value: 'weight_maintenance', label: 'Maintain Weight', emoji: '✨', description: 'Stay at current weight' },
  { value: 'muscle_gain', label: 'Build Muscle', emoji: '💪', description: 'Gain strength & muscle' },
  { value: 'general_wellness', label: 'General Wellness', emoji: '🌟', description: 'Overall health improvement' },
  { value: 'better_sleep', label: 'Better Sleep', emoji: '😴', description: 'Improve sleep quality' },
  { value: 'reduce_stress', label: 'Reduce Stress', emoji: '🧘', description: 'Manage stress better' },
  { value: 'increase_energy', label: 'More Energy', emoji: '⚡', description: 'Feel more energized' }
];

const CUISINES = [
  // Africa — culturally specific (avoid "West African" / "East African" as primary labels)
  { value: 'nigerian',      label: 'Nigerian',      emoji: '🍲' },
  { value: 'ghanaian',      label: 'Ghanaian',      emoji: '🍛' },
  { value: 'senegalese',    label: 'Senegalese',    emoji: '🥘' },
  { value: 'kenyan',        label: 'Kenyan',        emoji: '🌽' },
  { value: 'ethiopian',     label: 'Ethiopian',     emoji: '🫓' },
  { value: 'moroccan',      label: 'Moroccan',      emoji: '🥗' },
  { value: 'south_african', label: 'South African', emoji: '🍖' },
  // Caribbean
  { value: 'jamaican',      label: 'Jamaican',      emoji: '🌴' },
  { value: 'trinidadian',   label: 'Trinidadian',   emoji: '🍛' },
  // Mediterranean & Middle East
  { value: 'lebanese',      label: 'Lebanese',      emoji: '🧆' },
  { value: 'turkish',       label: 'Turkish',       emoji: '🥙' },
  { value: 'greek',         label: 'Greek',         emoji: '🫒' },
  { value: 'italian',       label: 'Italian',       emoji: '🍝' },
  // Europe
  { value: 'french',        label: 'French',        emoji: '🥖' },
  { value: 'british',       label: 'British',       emoji: '🥧' },
  // South Asia
  { value: 'indian_south',  label: 'South Indian',  emoji: '🍛' },
  { value: 'indian_north',  label: 'North Indian',  emoji: '🍛' },
  { value: 'pakistani',     label: 'Pakistani',     emoji: '🍢' },
  // East & Southeast Asia
  { value: 'japanese',      label: 'Japanese',      emoji: '🍣' },
  { value: 'chinese',       label: 'Chinese',       emoji: '🥟' },
  { value: 'korean',        label: 'Korean',        emoji: '🍲' },
  { value: 'vietnamese',    label: 'Vietnamese',    emoji: '🍜' },
  { value: 'thai',          label: 'Thai',          emoji: '🍲' },
  { value: 'filipino',      label: 'Filipino',      emoji: '🍚' },
  // Americas
  { value: 'american',      label: 'American',      emoji: '🥪' },
  { value: 'mexican',       label: 'Mexican',       emoji: '🌮' },
  { value: 'brazilian',     label: 'Brazilian',     emoji: '🍖' },
  { value: 'peruvian',      label: 'Peruvian',      emoji: '🐟' },
];

const DIETARY_RESTRICTIONS = [
  { value: 'vegetarian', label: 'Vegetarian', emoji: '🥬' },
  { value: 'vegan', label: 'Vegan', emoji: '🌱' },
  { value: 'halal', label: 'Halal', emoji: '☪️' },
  { value: 'kosher', label: 'Kosher', emoji: '✡️' },
  { value: 'gluten_free', label: 'Gluten-Free', emoji: '🌾' },
  { value: 'dairy_free', label: 'Dairy-Free', emoji: '🥛' },
  { value: 'pescatarian', label: 'Pescatarian', emoji: '🐟' }
];

const HEALTH_CONDITIONS = [
  { value: 'diabetes', label: 'Diabetes' },
  { value: 'hypertension', label: 'High Blood Pressure' },
  { value: 'heart_disease', label: 'Heart Disease' },
  { value: 'thyroid', label: 'Thyroid Issues' },
  { value: 'celiac', label: 'Celiac Disease' },
  { value: 'ibs', label: 'IBS' },
  { value: 'none', label: 'None' }
];

const OnboardingFlow = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    age_range: '',
    gender: '',
    country: '',
    cultural_region: '',
    primary_health_goal: '',
    health_goals: [],
    cuisine_preferences: [],
    dietary_restrictions: [],
    allergies: [],
    medical_conditions: [],
    track_medication: false,
    wants_specialist_access: false
  });

  const totalSteps = 5;

  const handleNext = () => {
    // Validation
    if (currentStep === 1 && !formData.age_range) {
      toast.error(t('onboarding.errorAgeRange'));
      return;
    }
    if (currentStep === 2 && !formData.country) {
      toast.error(t('onboarding.errorCountry'));
      return;
    }
    if (currentStep === 3 && !formData.primary_health_goal) {
      toast.error(t('onboarding.errorGoal'));
      return;
    }
    if (currentStep === 4 && formData.cuisine_preferences.length === 0) {
      toast.error(t('onboarding.errorCuisine'));
      return;
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Set wellness_focus based on primary goal
      const wellnessMapping = {
        'weight_loss': 'nutrition',
        'weight_maintenance': 'nutrition',
        'muscle_gain': 'physical_fitness',
        'general_wellness': 'nutrition',
        'better_sleep': 'sleep',
        'reduce_stress': 'stress_management',
        'increase_energy': 'nutrition'
      };

      const onboardingData = {
        ...formData,
        wellness_focus: wellnessMapping[formData.primary_health_goal] || 'nutrition',
        health_goals: formData.health_goals.length > 0 
          ? formData.health_goals 
          : [formData.primary_health_goal]
      };

      await healthApi.completeOnboarding(onboardingData);
      
      toast.success(t('onboarding.welcomeToast'));
      navigate('/dashboard');
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error(t('onboarding.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const toggleArrayValue = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const selectSingleValue = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-set cultural_region when country is selected
    if (field === 'country') {
      const country = COUNTRIES.find(c => c.value === value);
      if (country) {
        setFormData(prev => ({ ...prev, cultural_region: country.region }));
      }
    }
  };

  // Render steps
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('onboarding.step1Title')}</h2>
              <p className="text-gray-600">{t('onboarding.step1Subtitle')}</p>
            </div>

            {/* Age Range */}
            <div>
              <Label className="text-base font-semibold mb-3 block">{t('onboarding.ageRangeLabel')}</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {AGE_RANGES.map(age => (
                  <button
                    key={age.value}
                    onClick={() => selectSingleValue('age_range', age.value)}
                    className={`p-4 rounded-xl border-2 transition-all font-medium ${
                      formData.age_range === age.value
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    {age.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Gender (Optional) */}
            <div>
              <Label className="text-base font-semibold mb-3 block">{t('onboarding.genderLabel')}</Label>
              <div className="grid grid-cols-2 gap-3">
                {GENDERS.map(gender => (
                  <button
                    key={gender.value}
                    onClick={() => selectSingleValue('gender', gender.value)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      formData.gender === gender.value
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="text-2xl mb-1">{gender.emoji}</div>
                    <div className="text-sm font-medium">{t('onboarding.gender_' + gender.value)}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('onboarding.step2Title')}</h2>
              <p className="text-gray-600">{t('onboarding.step2Subtitle')}</p>
            </div>

            <div>
              <Label className="text-base font-semibold mb-3 block">{t('onboarding.countryLabel')}</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                {COUNTRIES.map(country => (
                  <button
                    key={country.value}
                    onClick={() => selectSingleValue('country', country.value)}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      formData.country === country.value
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="font-medium">{t('onboarding.country_' + country.value)}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('onboarding.step3Title')}</h2>
              <p className="text-gray-600">{t('onboarding.step3Subtitle')}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {HEALTH_GOALS.map(goal => (
                <button
                  key={goal.value}
                  onClick={() => {
                    selectSingleValue('primary_health_goal', goal.value);
                    if (!formData.health_goals.includes(goal.value)) {
                      toggleArrayValue('health_goals', goal.value);
                    }
                  }}
                  className={`p-5 rounded-xl border-2 transition-all text-left ${
                    formData.primary_health_goal === goal.value
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-3xl">{goal.emoji}</span>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{t('onboarding.goal_' + goal.value)}</div>
                      <div className="text-sm text-gray-600 mt-1">{t('onboarding.goalDesc_' + goal.value)}</div>
                    </div>
                    {formData.primary_health_goal === goal.value && (
                      <Check className="h-5 w-5 text-purple-600" />
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Additional goals */}
            <div className="pt-4 border-t">
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                {t('onboarding.additionalGoals')}
              </Label>
              <div className="flex flex-wrap gap-2">
                {HEALTH_GOALS.filter(g => g.value !== formData.primary_health_goal).map(goal => (
                  <button
                    key={goal.value}
                    onClick={() => toggleArrayValue('health_goals', goal.value)}
                    className={`px-3 py-2 rounded-lg border text-sm transition-all ${
                      formData.health_goals.includes(goal.value)
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-300 hover:border-purple-400'
                    }`}
                  >
                    {goal.emoji} {t('onboarding.goal_' + goal.value)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('onboarding.step4Title')}</h2>
              <p className="text-gray-600">{t('onboarding.step4Subtitle')}</p>
            </div>

            {/* Cuisine Preferences */}
            <div>
              <Label className="text-base font-semibold mb-3 block">{t('onboarding.cuisineLabel')}</Label>
              <p className="text-sm text-gray-600 mb-3">{t('onboarding.cuisineHint')}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {CUISINES.map(cuisine => (
                  <button
                    key={cuisine.value}
                    onClick={() => toggleArrayValue('cuisine_preferences', cuisine.value)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      formData.cuisine_preferences.includes(cuisine.value)
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="text-2xl mb-1">{cuisine.emoji}</div>
                    <div className="text-sm font-medium">{t('onboarding.cuisine_' + cuisine.value, cuisine.label)}</div>
                    {formData.cuisine_preferences.includes(cuisine.value) && (
                      <Check className="h-4 w-4 text-purple-600 mt-1 mx-auto" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Dietary Restrictions */}
            <div>
              <Label className="text-base font-semibold mb-3 block">{t('onboarding.dietaryLabel')}</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {DIETARY_RESTRICTIONS.map(diet => (
                  <button
                    key={diet.value}
                    onClick={() => toggleArrayValue('dietary_restrictions', diet.value)}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      formData.dietary_restrictions.includes(diet.value)
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    <div className="text-xl mb-1">{diet.emoji}</div>
                    <div className="text-xs font-medium">{t('onboarding.diet_' + diet.value)}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('onboarding.step5Title')}</h2>
              <p className="text-gray-600">{t('onboarding.step5Subtitle')}</p>
            </div>

            {/* Health Conditions */}
            <div>
              <Label className="text-base font-semibold mb-3 block">{t('onboarding.conditionsLabel')}</Label>
              <p className="text-sm text-gray-500 mb-3">{t('onboarding.conditionsHint')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {HEALTH_CONDITIONS.map(condition => (
                  <button
                    key={condition.value}
                    onClick={() => toggleArrayValue('medical_conditions', condition.value)}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      formData.medical_conditions.includes(condition.value)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{t('onboarding.condition_' + condition.value)}</span>
                      {formData.medical_conditions.includes(condition.value) && (
                        <Check className="h-4 w-4 text-blue-600" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Privacy Disclaimer */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-6 mt-6">
              <div className="flex items-start gap-3">
                <Shield className="h-6 w-6 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{t('onboarding.privacyTitle')}</h3>
                  <p className="text-sm text-gray-700">{t('onboarding.privacyBody')}</p>
                </div>
              </div>
            </div>

            {/* Optional checkboxes */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="track_medication"
                  checked={formData.track_medication}
                  onCheckedChange={(checked) =>
                    setFormData(prev => ({ ...prev, track_medication: checked }))
                  }
                />
                <label htmlFor="track_medication" className="text-sm text-gray-700 cursor-pointer">
                  {t('onboarding.trackMedication')}
                </label>
              </div>

              <div className="flex items-center gap-3">
                <Checkbox
                  id="wants_specialist_access"
                  checked={formData.wants_specialist_access}
                  onCheckedChange={(checked) =>
                    setFormData(prev => ({ ...prev, wants_specialist_access: checked }))
                  }
                />
                <label htmlFor="wants_specialist_access" className="text-sm text-gray-700 cursor-pointer">
                  {t('onboarding.specialistInterest')}
                </label>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              {t('onboarding.stepProgress', { step: currentStep, total: totalSteps })}
            </span>
            <span className="text-sm text-gray-500">
              {t('onboarding.percentComplete', { percent: Math.round((currentStep / totalSteps) * 100) })}
            </span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-600 to-indigo-600 transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Main Card */}
        <Card className="p-6 md:p-8">
          {renderStep()}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t">
            <Button
              onClick={handleBack}
              variant="outline"
              disabled={currentStep === 1}
              className="px-6"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              {t('common.back')}
            </Button>

            <Button
              onClick={handleNext}
              disabled={loading}
              className="px-8 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
            >
              {loading ? (
                t('common.saving')
              ) : currentStep === totalSteps ? (
                <>
                  {t('onboarding.completeSetup')}
                  <Check className="h-4 w-4 ml-2" />
                </>
              ) : (
                <>
                  {t('common.next')}
                  <ChevronRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingFlow;
